"use client";

import { useRef, useEffect, useState } from 'react';
import Image from 'next/image';
import { noto_sans } from '@/globalConstants';
import { Testimonial } from '@/globalTypes';

interface InfiniteScrollTestimonialsProps {
  testimonials: Testimonial[];
}

const COPIES = 6;

export default function InfiniteScrollTestimonials({ testimonials }: InfiniteScrollTestimonialsProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // The server HTML carries one copy of the testimonials. The copies the
  // marquee needs to loop are added after mount: rendered on the server they
  // put the same reviews into every page's HTML six times over.
  const [copies, setCopies] = useState(1);
  useEffect(() => setCopies(COPIES), []);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    const content = contentRef.current;
    
    if (!scrollContainer || !content || copies < 2) return;

    let scrollPosition = 0;
    let animationFrameId: number | null = null;
    let isPaused = false;

    const scroll = () => {
      if (isPaused) return;
      
      scrollPosition += 1;
      
      // Width of a single set of testimonials.
      const singleSetWidth = content.scrollWidth / copies;
      
      // Reset when we've scrolled through one set (seamless loop)
      if (scrollPosition >= singleSetWidth) {
        scrollPosition = 0;
      }
      
      scrollContainer.scrollLeft = scrollPosition;
      animationFrameId = requestAnimationFrame(scroll);
    };

    // Start scrolling after a brief delay
    const startAnimation = () => {
      animationFrameId = requestAnimationFrame(scroll);
    };
    
    // Wait for content to be laid out
    setTimeout(startAnimation, 100);

    // Pause on hover
    const handleMouseEnter = () => {
      isPaused = true;
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }
    };

    const handleMouseLeave = () => {
      isPaused = false;
      if (animationFrameId === null) {
        animationFrameId = requestAnimationFrame(scroll);
      }
    };

    scrollContainer.addEventListener('mouseenter', handleMouseEnter);
    scrollContainer.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
      scrollContainer.removeEventListener('mouseenter', handleMouseEnter);
      scrollContainer.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [testimonials, copies]);

  const duplicatedTestimonials = Array.from({ length: copies }, () => testimonials).flat();

  return (
    <div className="w-full overflow-hidden mt-4">
      <div
        ref={scrollContainerRef}
        className="flex gap-6 overflow-x-auto"
        style={{
          scrollBehavior: 'auto',
        }}
      >
        <div ref={contentRef} className="flex gap-6">
          {duplicatedTestimonials.map((testimonial, index) => (
            <div
              key={index}
              aria-hidden={index >= testimonials.length || undefined}
              className="flex-shrink-0 w-[260px] md:w-[260px] bg-white rounded-2xl p-6 flex flex-col gap-3"
            >
              <div className="flex items-start gap-2">
                <Image
                    src={`/testimonial-icon.png`}
                    alt="testimonial-icon"
                    width={40}
                    height={40}
                  />
              </div>
              <p className={`${noto_sans.className} text-[#1D4749] text-md sm:text-sm md:text-lg lg:text-md flex-grow`}>
                {testimonial.review}
              </p>
              <div className="flex items-center gap-1">
                {[...Array(testimonial.stars)].map((_, i) => (
                  <Image
                    key={i}
                    src={`/testimonial-star.svg`}
                    alt="star"
                    width={16}
                    height={16}
                  />
                ))}
              </div>
              <div className={`${noto_sans.className} text-[#1D4749] text-sm flex flex-col`}>
                <p className='font-semibold'>{testimonial.author}</p>
                <p>{testimonial.location}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

